import type { RefObject } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chart as ChartJS } from 'chart.js';

import { MetricChart, useMetricChart } from '@/components/charts/generic';
import ChartActionsMenu from '@/components/molecules/ChartActionsMenu';
import ContentBlock from '@/components/molecules/ContentBlock';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import {
    DATE_RANGE_PRESETS,
    type DateRangePresetId,
    findMatchingPreset,
} from '@/components/molecules/DateRangePicker/DateRangePresets';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import type { DepthResolution } from '@/domain/depthSeries';
import { clampHourlyRange, useGetCageDepthSeries } from '@/domain/depthSeries';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { parseLocale } from '@/utils/localeFormatters';

import { buildBioscopeDepthChart } from './buildBioscopeDepthChart';

import styles from './BioscopeDepthCard.module.scss';

const CHART_HEIGHT = 280;
const FULL_SCREEN_CHART_HEIGHT = 520;

/** Left off the hourly menu. Daily still offers them. */
const HOURLY_HIDDEN_PRESETS: readonly DateRangePresetId[] = ['last4weeks', 'last3months', 'all'];

interface BioscopeDepthCardProps {
    cageId: number;
}

function initialRange(): DateRange {
    return DATE_RANGE_PRESETS.find((preset) => preset.id === 'last7days')?.calculateDates() ?? {};
}

/**
 * Stays hidden until hourly depth is on. The chart is a child so its hooks only run once there
 * is something to draw.
 */
export default function BioscopeDepthCard(props: BioscopeDepthCardProps) {
    const { enabled } = usePostHogFeatureFlag('hourly-depth');
    if (!enabled) return null;
    return <BioscopeDepthChart cageId={props.cageId} />;
}

function BioscopeDepthChart(props: BioscopeDepthCardProps) {
    const { cageId } = props;
    const { t, i18n } = useTranslation();
    const [resolution, setResolution] = useState<DepthResolution>('hour');
    const [range, setRange] = useState<DateRange>(initialRange);
    const from = range.dateFrom ?? '';
    const to = range.dateTo ?? '';
    const canFetch = from !== '' && to !== '';
    const hourlyView = resolution === 'hour';
    // A custom range can still run past 31 days after the long presets are hidden.
    const hourlyWindow = canFetch ? clampHourlyRange(from, to, to) : { from, to, clamped: false };
    const fetchFrom = hourlyView ? hourlyWindow.from : from;
    const fetchTo = hourlyView ? hourlyWindow.to : to;

    const hourly = useGetCageDepthSeries(
        cageId,
        { from: fetchFrom, to: fetchTo, resolution: 'hour' },
        { enabled: canFetch && hourlyView }
    );
    const daily = useGetCageDepthSeries(
        cageId,
        { from: fetchFrom, to: fetchTo, resolution: 'day' },
        { enabled: canFetch }
    );

    const spec = useMemo(
        () =>
            buildBioscopeDepthChart({
                resolution,
                hourly: hourly.data.cages[0]?.points ?? [],
                daily: daily.data.cages[0]?.points ?? [],
                locale: parseLocale(i18n.language),
                labels: {
                    hourly: t('bioscopeDepth.hourlyAverage'),
                    daily: t('bioscopeDepth.dailyAverage'),
                },
            }),
        [resolution, hourly.data.cages, daily.data.cages, i18n.language, t]
    );
    const chart = useMetricChart(spec);
    const chartHeight = chart.isFullScreen
        ? Math.max(CHART_HEIGHT, FULL_SCREEN_CHART_HEIGHT)
        : CHART_HEIGHT;
    const loading = hourlyView ? hourly.isLoading || daily.isLoading : daily.isLoading;
    const error = hourlyView ? (hourly.error ?? daily.error) : daily.error;

    function retry() {
        if (hourlyView) hourly.refetch();
        daily.refetch();
    }

    function handleResolution(value: DepthResolution) {
        setResolution(value);
        if (value !== 'hour') return;
        const preset = findMatchingPreset(from, to, { includeLast3Days: true });
        if (preset !== null && HOURLY_HIDDEN_PRESETS.includes(preset)) {
            setRange(initialRange());
        }
    }

    return (
        <ContentBlock
            title={t('bioscopeDepth.title')}
            containerRef={chart.fullScreenContainerRef}
            headerClassName={styles.header}
            headerActions={
                <div className={styles.controls}>
                    <SegmentedControl<DepthResolution>
                        className={styles.resolution}
                        aria-label={t('bioscopeDepth.resolution')}
                        value={resolution}
                        onChange={handleResolution}
                        options={[
                            { value: 'hour', label: t('bioscopeDepth.hourly') },
                            { value: 'day', label: t('bioscopeDepth.daily') },
                        ]}
                    />
                    <div className={styles.range}>
                        <DateRangePicker
                            dateFrom={range.dateFrom}
                            dateTo={range.dateTo}
                            defaultPresetId="last7days"
                            show24hPreset
                            showLast3DaysPreset
                            hiddenPresetIds={hourlyView ? HOURLY_HIDDEN_PRESETS : undefined}
                            onDateChange={setRange}
                        />
                    </div>
                    <div className={styles.menu}>
                        <ChartActionsMenu
                            // Chart.js generics are invariant, so Chart<'line'> is not assignable to
                            // Chart<'line' | 'bar'>. The menu only reads the canvas and the data.
                            chartRef={chart.chartRef as RefObject<ChartJS<'line' | 'bar'> | null>}
                            chartName="bioscope-depth"
                            isFullScreen={chart.isFullScreen}
                            onFullScreenToggle={chart.handleFullScreenToggle}
                            disabled={loading}
                        />
                    </div>
                </div>
            }>
            {hourlyView && hourlyWindow.clamped && (
                <p className={styles.note}>{t('locationCompare.hourlyRangeNote')}</p>
            )}
            <MetricChart
                kind="line"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                height={chartHeight}
                isLoading={loading}
                error={error}
                onRetry={retry}
                isRetrying={hourly.isFetching || daily.isFetching}
                title={t('bioscopeDepth.title')}
            />
        </ContentBlock>
    );
}
